import type { DatasetConfig } from '../types';

export const wellsConfig: DatasetConfig = {
  id: 'wells',
  usesGlobalText: true,
  label: 'Wells',
  searchEndpoint: '/queries/wells',
  // Joins the Map Query spatial-export roster as the backend 'wells' dataset. Attribute ids match
  // GisExport::Sources::MapQuery::WELL_COLUMNS (the dialog offers only backend-supported attributes).
  spatialExport: {
    key: 'wells',
    attributes: [
      { id: 'uwi', label: 'UWI' },
      { id: 'name', label: 'Well Name' },
      { id: 'current_operator', label: 'Operator' },
      { id: 'hole_direction', label: 'Hole Direction' },
      { id: 'status', label: 'Status' },
      { id: 'measured_depth_feet', label: 'Measured Depth (ft)' },
      { id: 'true_vertical_depth_feet', label: 'True Vertical Depth (ft)' },
      { id: 'permit_date', label: 'Permit Date' },
      { id: 'spud_date', label: 'Spud Date' },
      { id: 'completion_date', label: 'Completion Date' },
      { id: 'rig_release_date', label: 'Rig Release Date' },
      { id: 'created_at', label: 'Created At' },
      { id: 'updated_at', label: 'Updated At' },
    ],
  },
  tileEndpoint: '/queries/wells/mvt',
  geoLocationKey: 'surfaceHole',
  canCreateDcaWorkspace: true,
  reconcilable: true,
  // Checked wells wrap into a new area, sent to the backend by row id.
  areaSelection: {
    kind: 'wells',
    singular: 'Well',
    geometry: 'point',
    idKey: 'id',
    nameKey: 'name',
  },
  // Carbon Blue 60 — matches the mock's Wells layer swatch (was falling back to black).
  defaultLayerColor: '#0f62fe',
  // Attributes present as MVT tile properties (values are uppercase on tiles).
  colorByAttributes: [
    {
      facetKey: 'status',
      label: 'Status',
      mvtProperty: 'status',
      rowField: 'status',
    },
    {
      facetKey: 'holeDirection',
      label: 'Hole Direction',
      mvtProperty: 'hole_direction',
      rowField: 'holeDirection',
    },
    // "State" + "Operator" live on the modal's Common tab (sourced from the wells
    // facets), so they carry wells-space tokens — state abbreviation and the raw
    // operator string. Both are scalar tile props, so they recolor map + cards.
    {
      facetKey: 'state',
      label: 'State',
      mvtProperty: 'state_abbreviation',
      rowField: 'stateCode',
    },
    {
      facetKey: 'currentOperator',
      label: 'Operator',
      mvtProperty: 'current_operator',
      rowField: 'currentOperator',
    },
    /* Provenance: 'Insights' for a well authored in-app, 'ihs' for one ingested.
       THE TWO KEYS DIFFER ON PURPOSE. The tile property is the raw ES field, `source`;
       the row field is `wellSource`, because the row's own `source` is already the survey
       geometry's source — a different thing that co-exists on the same row. Jbuilder
       camelizes, so the backend's `json.well_source` arrives here as `wellSource` (the
       same reason `state_code` is read as `stateCode` above). Matching these two up by
       eye is how this silently stops painting. */
    {
      facetKey: 'source',
      label: 'Source',
      mvtProperty: 'source',
      rowField: 'wellSource',
    },
  ],
  // The facet aggregations are the slow part of this endpoint; fetch them in a dedicated
  // request so map panning and result paging stay fast.
  facetsMode: 'separate',
  // Lean: the facets request reads counts only, so the backend skips its row.
  facetsPayload: { response_config: { lean_facets: true } },
  defaultPayload: {
    use_elastic: true,
    text: '',
    page: 1,
    pageSize: 100,
    // Results-only main search; facets come from the separate request above. Keep the bounding
    // box: FitBoundsOnSearch fits the map to it in the No-Map-Filter / AOI spatial modes.
    response_config: { include_facets: false, include_bounding_box: true },
    // The "Potential duplicates" banner count is scoped to the current filter set, so it tracks the
    // listing (and the area-scoped datasets view reads an area-local figure); with no filters it
    // degrades to the run-wide total. Sent on every wells search.
    include_scoped_duplicate_count: true,
  },

  filters: [
    // ── Current Operator ──
    {
      id: 'current_operator',
      label: 'Current Operator',
      category: 'Current Operator',
      type: 'multi-select-text-search-combo',
      param: 'current_operator_ids',
      textParam: 'current_operator',
      facetKey: 'currentOperator',
      selectAll: true,
      pasteableIds: true,
    },

    // ── Hole Direction ──
    {
      id: 'hole_direction',
      label: 'Hole Direction',
      category: 'Hole Direction',
      type: 'picklist',
      param: 'hole_direction_ids',
      facetKey: 'holeDirection',
    },

    // ── Well Name ──
    {
      id: 'name',
      label: 'Well Name',
      category: 'Well Name',
      type: 'multi-select-text-search-combo',
      param: 'name_ids',
      textParam: 'name',
      facetKey: 'wellName',
      selectAll: true,
      pasteableIds: true,
    },

    // ── SIM Landing Zone ──
    {
      id: 'srp_landing_zone',
      label: 'SIM Landing Zone',
      category: 'SIM Landing Zone',
      type: 'multi-select-text-search-combo',
      param: 'srp_landing_zone_ids',
      textParam: 'srp_landing_zone',
      facetKey: 'srpLandingZone',
      selectAll: true,
      pasteableIds: true,
    },

    // ── State/County ──
    {
      id: 'state',
      label: 'State',
      category: 'State',
      type: 'multi-select',
      param: 'state_abbreviation_ids',
      facetKey: 'state',
    },

    // ── Source ──
    /* The well's PROVENANCE: 'Insights' for one authored in-app, 'ihs' for one ingested
       from the vendor feed. The param is `sources`, NOT `source_ids` — the generic facet
       helper would splice in `source_ids`, which is already taken by the DCA-forecast
       nested filter, so the backend hand-rolls this one.

       The two values are cased asymmetrically ('ihs' vs 'Insights'), and they match a
       keyword field literally. The ids below never pass through this app: the facet serves
       both the id and its display name ('IHS'), so the bucket travels verbatim. Tidying
       either one here would match nothing and return an empty result rather than erroring. */
    {
      id: 'source',
      label: 'Source',
      category: 'Source',
      type: 'picklist',
      param: 'sources',
      facetKey: 'source',
    },

    // ── Status ──
    {
      id: 'status',
      label: 'Status',
      category: 'Status',
      type: 'picklist',
      param: 'status_ids',
      facetKey: 'status',
    },

    // ── Potential Duplicates ──
    // A toggle: keep only wells the detector flagged as a potential duplicate of another
    // (duplicate_count > 0, the same ES field the Potential Duplicates column sorts on).
    {
      id: 'potential_duplicates',
      label: 'Has potential duplicates',
      category: 'Potential Duplicates',
      type: 'checkbox',
      param: 'has_potential_duplicates',
      countFrom: 'duplicateTotal',
    },

    // ── Confirmed Duplicates ──
    // A toggle, default off: show ONLY the wells reconciled (merged) into a survivor. A merge hides
    // the duplicate from the normal listing, so this is how a user finds and reaches one. The backend
    // (has_confirmed_duplicates) flips its deduplicated-well exclusion into an inclusion.
    {
      id: 'confirmed_duplicates',
      label: 'Show confirmed duplicates',
      category: 'Confirmed Duplicates',
      type: 'checkbox',
      param: 'has_confirmed_duplicates',
    },

    // ── UWI ──
    {
      id: 'uwi',
      label: 'UWI',
      category: 'UWI',
      type: 'multi-select-text-search-combo',
      param: 'uwi_ids',
      textParam: 'uwi',
      facetKey: 'uwi',
      pasteableIds: true,
    },

    // ── Well Dates ──
    {
      id: 'permit_date',
      label: 'Permit Date',
      category: 'Well Dates',
      type: 'date-range',
      minParam: 'min_permit_date',
      maxParam: 'max_permit_date',
    },
    {
      id: 'spud_date',
      label: 'Spud Date',
      category: 'Well Dates',
      type: 'date-range',
      minParam: 'min_spud_date',
      maxParam: 'max_spud_date',
    },
    {
      id: 'completion_date',
      label: 'Completion Date',
      category: 'Well Dates',
      type: 'date-range',
      minParam: 'min_completion_date',
      maxParam: 'max_completion_date',
    },
    {
      id: 'rig_release_date',
      label: 'Rig Release Date',
      category: 'Well Dates',
      type: 'date-range',
      minParam: 'min_rig_release_date',
      maxParam: 'max_rig_release_date',
    },
    {
      id: 'first_production_date',
      label: 'First Production Date',
      category: 'Well Dates',
      type: 'date-range',
      minParam: 'min_first_production_date',
      maxParam: 'max_first_production_date',
    },
    {
      id: 'created_at',
      label: 'Created',
      category: 'Well Dates',
      type: 'date-range',
      minParam: 'min_created_at',
      maxParam: 'max_created_at',
    },
    {
      id: 'updated_at',
      label: 'Updated',
      category: 'Well Dates',
      type: 'date-range',
      minParam: 'min_updated_at',
      maxParam: 'max_updated_at',
    },

    // ── Well Depths ──
    {
      id: 'measured_depth',
      label: 'Measured Depth (ft)',
      category: 'Well Depths',
      type: 'numeric-range',
      minParam: 'min_measured_depth',
      maxParam: 'max_measured_depth',
    },
    {
      id: 'true_vertical_depth',
      label: 'True Vertical Depth (ft)',
      category: 'Well Depths',
      type: 'numeric-range',
      minParam: 'min_true_vertical_depth',
      maxParam: 'max_true_vertical_depth',
    },
  ],

  columns: [
    {
      id: 'uwi',
      header: 'UWI',
      accessorKey: 'uwi',
      size: 190,
      sortable: true,
      type: 'link',
      hrefKey: 'href',
    },
    {
      id: 'name',
      header: 'Well Name',
      accessorKey: 'name',
      size: 250,
      sortable: true,
    },
    {
      id: 'current_operator',
      header: 'Operator',
      accessorKey: 'currentOperator',
      size: 200,
      sortable: true,
      type: 'link',
      hrefKey: 'currentOperatorHref',
    },
    {
      id: 'hole_direction',
      header: 'Hole Direction',
      accessorKey: 'holeDirection',
      size: 190,
      sortable: true,
    },
    {
      id: 'state_abbreviation',
      header: 'State',
      accessorKey: 'stateAbbreviation',
      size: 140,
      sortable: true,
    },
    {
      id: 'srp_landing_zone',
      header: 'SIM Landing Zone',
      accessorKey: 'srpLandingZone',
      size: 220,
      sortable: true,
    },
    {
      id: 'measured_depth_feet',
      header: 'Measured Depth (ft)',
      accessorKey: 'measuredDepthFeet',
      size: 230,
      sortable: true,
      type: 'number',
      decimals: 0,
    },
    {
      id: 'true_vertical_depth_feet',
      header: 'True Vertical Depth (ft)',
      accessorKey: 'trueVerticalDepthFeet',
      size: 260,
      sortable: true,
      type: 'number',
      decimals: 0,
    },
    {
      id: 'permit_date',
      header: 'Permit Date',
      accessorKey: 'permitDate',
      size: 170,
      sortable: true,
      type: 'date',
    },
    {
      id: 'spud_date',
      header: 'Spud Date',
      accessorKey: 'spudDate',
      size: 160,
      sortable: true,
      type: 'date',
    },
    {
      id: 'completion_date',
      header: 'Completion Date',
      accessorKey: 'completionDate',
      size: 200,
      sortable: true,
      type: 'date',
    },
    {
      id: 'first_production_date',
      header: 'On Production',
      accessorKey: 'firstProductionDate',
      size: 190,
      sortable: true,
      type: 'date',
    },
    {
      id: 'rig_release_date',
      header: 'Rig Release Date',
      accessorKey: 'rigReleaseDate',
      size: 200,
      sortable: true,
      type: 'date',
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      size: 150,
      sortable: true,
    },
    {
      id: 'created_at',
      header: 'Created',
      accessorKey: 'createdAt',
      size: 160,
      sortable: true,
      type: 'date',
    },
    {
      id: 'updated_at',
      header: 'Updated',
      accessorKey: 'updatedAt',
      size: 160,
      sortable: true,
      type: 'date',
    },
  ],

  tileLayers: [
    {
      geoAttribute: 'survey',
      layerType: 'line',
      minZoom: 8,
      labelMinZoom: 12,
      labelFields: ['name'],
    },
    {
      geoAttribute: 'stick',
      layerType: 'line',
      minZoom: 8,
      labelMinZoom: 12,
      labelFields: ['name'],
      dashArray: [6, 2],
    },
    // Horizontal wells → circles
    {
      geoAttribute: 'surface_hole',
      layerType: 'circle',
      minZoom: 0,
      hitTarget: true,
      filter: [
        'in',
        'hole_direction',
        'HORIZONTAL',
        'HORIZONTAL MINING BOREHOLE',
      ],
    },
    {
      geoAttribute: 'bottom_hole',
      layerType: 'circle',
      minZoom: 12,
      hitTarget: true,
      filter: [
        'in',
        'hole_direction',
        'HORIZONTAL',
        'HORIZONTAL MINING BOREHOLE',
      ],
    },
    // Vertical wells → symbols (● for most, × for shut_in)
    {
      geoAttribute: 'surface_hole',
      layerType: 'symbol',
      minZoom: 0,
      hitTarget: true,
      filter: ['in', 'hole_direction', 'VERTICAL', 'DIRECTIONAL', 'PINNATE'],
      textField: ['match', ['get', 'status'], 'SHUT_IN', '×', '●'],
    },
    {
      geoAttribute: 'bottom_hole',
      layerType: 'symbol',
      minZoom: 12,
      hitTarget: true,
      filter: ['in', 'hole_direction', 'VERTICAL', 'DIRECTIONAL', 'PINNATE'],
      textField: ['match', ['get', 'status'], 'SHUT_IN', '×', '●'],
    },
  ],
};
